import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    Paper,
    TextField,
} from "@mui/material";
import { useState, useEffect, useMemo } from "react";
import { getLiquidationStockDetails, getStoreProducts } from "../../../controllers/StockController";
import moment from "moment";

export default function AddProductsModal({
    modalOpen,
    setModalOpen,
    storeId,
    loadingProducts,
    handleConfirmAdd,
    handleCloseModal,
    setApiMessage,
    stoppageCriteriaEnums,
    ruleTypeEnums,
}) {

    const [storeProducts, setStoreProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [searchText, setSearchText] = useState("");

    const handleProductToggle = (productId) => {
        if (selectedProducts.includes(productId)) {
            setSelectedProducts(selectedProducts.filter((id) => id !== productId));
        } else {
            setSelectedProducts([...selectedProducts, productId]);
        }
    };

    useEffect(() => {
        if (modalOpen) {
            getProducts(storeId);
        }
    }, [modalOpen]);

    const filteredProducts = useMemo(() => {
        const q = searchText.trim().toLowerCase();
        if (!q) return storeProducts;

        return storeProducts.filter((p) => {
            const id = String(p.productItemId || p.id || "").toLowerCase();
            const name = String(p.name || p.productItemName || "").toLowerCase();
            return id.includes(q) || name.includes(q);
        });
    }, [storeProducts, searchText]);

    const getProducts = async (storeId) => {
        setLoading(true);
        try {
            const response = await getStoreProducts(storeId, ruleTypeEnums.LIQUIDATION_SALE);
            if (response) {
                // Assuming the API returns an array of products in response.data
                const products = response;
                setStoreProducts(products);
            } else {
                setStoreProducts([]);
            }
        } catch (error) {
            console.error("Error fetching store products:", error);
            setApiMessage({
                open: true,
                type: "error",
                message: error?.message || "Failed to fetch store products",
            });
            setStoreProducts([]);
        } finally {
            setLoading(false);
        }
    }
    const handleAdd = async () => {
        setLoading(true);
        try {
            const response = await getLiquidationStockDetails(storeId, selectedProducts);
            const newProducts = response;
            handleConfirmAdd(newProducts.map(p => ({
                ...p,
                qty: "",
                productItemName: storeProducts.find(sp => sp.productItemId == p.productItemId)?.name,
                startDate: moment().format("YYYY-MM-DD"),
                startTime: moment().add(5, "minutes").format("HH:mm:ss"),
                stoppageCriteria: stoppageCriteriaEnums[0],
                lastStockRefreshTimestamp: p.lastUpdatedTime,
                applicableSellingPrice: p.liquidationPrice,
                enabled: true,
                isAdded: true,
            })))
        } catch (err) {
            setApiMessage({
                open: true,
                type: "error",
                message: err?.message || "Failed to fetch liquidation stock details",
            });
        } finally {
            setLoading(false);
        }
    }

    return (
        <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="lg" fullWidth>
            <DialogTitle>Select Products to Add</DialogTitle>
            <DialogContent dividers>
                <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Search product by ID or Name"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                </Box>

                {loadingProducts ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : filteredProducts.length === 0 ? (
                    <Typography color="textSecondary">No products available</Typography>
                ) : (
                    <TableContainer component={Paper} variant="outlined"
                        sx={{
                            minHeight: 340,     // keeps container height even with few rows
                            maxHeight: 700,     // optional: scroll after this height
                            overflowY: "auto",
                        }}>
                        <Table size="small" stickyHeader sx={{
                            "& .MuiTableCell-root": {
                                py: 1.5,
                            },
                        }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">

                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Product Item ID</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredProducts.map((product) => {
                                    const productId = +product.productItemId || +product.id;
                                    const isChecked = selectedProducts.includes(productId);

                                    return (
                                        <TableRow
                                            key={productId}
                                            hover
                                            onClick={() => handleProductToggle(productId)}
                                            sx={{ cursor: "pointer" }}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox checked={isChecked} tabIndex={-1} disableRipple />
                                            </TableCell>
                                            <TableCell>{product.productItemId || product.id || "-"}</TableCell>
                                            <TableCell>{product.name || product.productItemName || "-"}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCloseModal} variant="outlined" color="error">Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleAdd}
                    disabled={selectedProducts.length === 0}
                >
                    Add Selected ({selectedProducts.length})
                </Button>
            </DialogActions>
        </Dialog>
    );

}